import React from "react";
import useIcons from "@hooks/use-source-icons";
import {
  ColorFormat,
  CountdownCircleTimer,
} from "react-countdown-circle-timer";
import Title from "@components/common/Title";
import {
  OneTimedFeatureModel,
  TimedFeatureListVariant,
} from "@models/strapi-components/timed-feature-list";
import { motion } from "framer-motion";

export interface TimedFeaturePropsModel {
  key: string | number;
  index: number;
  timedFeature: OneTimedFeatureModel;
  color: string;
  selectedFeatureId: string;
  setSelectedFeatureIndex: React.Dispatch<React.SetStateAction<number>>;
  progressEmptyColor: ColorFormat;
  progressFillColor: ColorFormat;
  times: number;
  variant: TimedFeatureListVariant;
}
const TimedFeature: React.FunctionComponent<TimedFeaturePropsModel> = ({
  index,
  timedFeature,
  selectedFeatureId,
  color = "",
  setSelectedFeatureIndex,
  progressEmptyColor = "#ffffff",
  progressFillColor = "#000000",
  times = 5,
  variant,
}) => {
  const { id, featureTitle, featureSubTitle, featureIcon } = timedFeature;
  const { getFromReactIcons } = useIcons();
  const Icon = getFromReactIcons(featureIcon);

  const isSelected = id === selectedFeatureId;
  const style = { backgroundColor: isSelected ? "transparent" : "inherit" };

  const renderLinearVariant = () => {
    return (
      <div className="flex flex-col items-center p-6 rounded-xl relative overflow-hidden bg-white max-w-[360px] shadow-[0px_10px_20px_0px_rgba(0,0,0,0.06)]">
        <div className="shrink-0 w-full mb-4">{Icon && <Icon size={28} />}</div>
        <div className="grow">
          <Title variant="h3" titleText={featureTitle} className="" />
          <Title variant="paragraph" titleText={featureSubTitle} />
        </div>
        <motion.div
          className="absolute bottom-0 left-0 bg-[#728CA7] w-0 h-2"
          animate={{
            width: isSelected ? "100%" : "0",
          }}
          transition={{ duration: isSelected ? times : 0 }}
        ></motion.div>
      </div>
    );
  };

  const renderCircularVariant = () => {
    return (
      <div
        className="flex items-center p-6 rounded-xl relative overflow-hidden"
        style={{
          backgroundColor: isSelected ? color : "inherit",
        }}
      >
        {isSelected ? (
          <div className="mr-4">
            <CountdownCircleTimer
              isPlaying={isSelected}
              duration={times}
              colors={progressFillColor}
              size={60}
              strokeWidth={4}
              isGrowing
              rotation="counterclockwise"
              trailColor={progressEmptyColor}
              onComplete={() => {
                return { shouldRepeat: true, delay: 1 };
              }}
            >
              {() => <>{Icon && <Icon size={28} />}</>}
            </CountdownCircleTimer>
          </div>
        ) : (
          <div
            className="shrink-0 flex items-center justify-center h-[60px] w-[60px]  rounded-full border-4 mr-4"
            style={{ borderColor: color }}
          >
            {Icon && <Icon size={28} />}
          </div>
        )}
        <div className="grow">
          <Title variant="h3" titleText={featureTitle} className="" />
          <Title variant="paragraph" titleText={featureSubTitle} />
        </div>
      </div>
    );
  };

  const renderVariant = () => {
    switch (variant) {
      case TimedFeatureListVariant.CIRCULAR:
        return renderCircularVariant();
      case TimedFeatureListVariant.LINEAR:
        return renderLinearVariant();
      default:
        return renderCircularVariant();
    }
  };

  return (
    <div
      className="flex max-w-[551px] cursor-pointer rounded-xl items-center relative bg-transparent mb-4"
      style={{ ...style }}
      onClick={() => {
        setSelectedFeatureIndex(index);
      }}
    >
      {renderVariant()}
    </div>
  );
};
export default TimedFeature;
